import React, { DragEvent, useState } from 'react';
import { GripVertical, Edit3, Trash2, ChevronRight, Ungroup, Flag, AlertTriangle } from 'lucide-react';
import RowActionsMenu, { RowAction } from './RowActionsMenu';
import utilityService from '../../../shared/utils/utilityService';
import { formatDuration, calculateProgress, calculateTimeRemaining, URGENCY_COLORS } from '@thekwallace/backtime-shared';
import type { RunningElementStatus } from '@thekwallace/backtime-shared';
import { formatVarianceWords, varianceColorClass, varianceGlyph, varianceTone } from '../utils/runningState';

type ElementState = 'expired' | 'active' | 'upcoming';
type UrgencyLevel = 'imminent' | 'soon' | 'moderate' | 'distant';

interface TimeElement {
  id: string;
  description: string;
  duration: number;
  startTime: number;
  endTime: number;
  countdown?: number;
}

/**
 * What the RUN says about this row, as opposed to what the schedule says.
 *
 * One object rather than nine flat props because these fields are a single coherent
 * unit — they are all absent together outside a run, and a row that has some of them
 * and not others is a bug rather than a state.
 */
export interface RunRowModel {
  /** In progress: elapsed against the planned budget, and when it is due to hand off. */
  elapsed?: string;
  budget?: string;
  dueToEndClock?: string;
  /** 0..1+, where >1 means the element has run past its budget. */
  progress?: number;
  /** Set once elapsed passes budget: how far over, already formatted. */
  overBy?: string;
  /** Time left in the element in progress, formatted. Absent once it is over. */
  remaining?: string;
  /** Completed: how long it actually ran, and the span it occupied. */
  ranFor?: string;
  spanFrom?: string;
  spanTo?: string;
  /** Fixed start (anchor): the wall-clock time it holds, and the risk to it. */
  anchorClock?: string;
  anchorRisk?: string | null;
  /** True when the displayed start is a projection that will move as the run drifts. */
  projected?: boolean;
}

interface TimeElementCardProps {
  element: TimeElement;
  state: ElementState;
  isNextUp?: boolean;
  urgency?: UrgencyLevel;
  showCountdown?: boolean;
  showActualTimes?: boolean;
  formattedStartTime?: string;
  currentTime?: number;
  onEdit?: () => void;
  onDelete?: () => void;
  draggable?: boolean;
  onDragStart?: (e: DragEvent<HTMLDivElement>) => void;
  onDragEnd?: (e: DragEvent<HTMLDivElement>) => void;
  onDragOver?: (e: DragEvent<HTMLDivElement>) => void;
  isDragging?: boolean;
  durationNote?: string;
  offsetInfo?: string;
  // Group variant (collapsed group renders like a normal element with a disclosure chevron)
  isGroup?: boolean;
  expanded?: boolean;
  onToggleExpand?: () => void;
  onUngroup?: () => void;
  itemCount?: number;
  // Edit mode reveals the per-row overflow (⋯) menu; operate mode hides all row actions.
  editMode?: boolean;
  // ---- Running state (Operate Mode) ----
  // ALL OPTIONAL by design: a card outside a running occurrence receives none of
  // them and renders exactly as it did before running state existed.
  /** Seconds this element is projected to run behind (+) or ahead (−) of plan. */
  variance?: number;
  /**
   * Which phase of a run this card belongs to, or undefined when there is no run.
   *
   * Deliberately NOT a boolean `isRunning`. Gating on "is running" made every
   * run-aware branch below switch off the instant the operator pressed Complete,
   * handing the row back to the planned-window-vs-clock derivation — now fed by
   * ACTUAL times — so a finished run rendered a live progress bar counting down an
   * element that had already happened. A completed run is still a run: it must
   * still say how it went, it just must not say anything is happening now.
   */
  runPhase?: 'running' | 'completed';
  /** Where this element sits relative to the operator's progress. */
  runStatus?: RunningElementStatus;
  /** Mark this element as having actually started now. Owner only. */
  onMark?: () => void;
  /**
   * Whether the WHOLE ROW is a mark target, not just the flag in the gutter.
   *
   * Owner-only and gated at the element level rather than by hiding the handler: a
   * viewer's row must not get a pointer cursor, a role, or a focus ring either, or the
   * affordance leaks even though nothing happens on click.
   *
   * Marking row N implicitly says everything before N is done — which the projection
   * already derives — so one gesture both records a transition and repairs a cursor
   * that has drifted, with no quantity for the operator to decide mid-service.
   */
  rowIsMarkTarget?: boolean;
  /** Live facts for this row. Absent outside a run. */
  runRow?: RunRowModel;
  /**
   * Shift this element's recorded start by N seconds. Owner only, and only on a row
   * that actually has a recorded start.
   *
   * This is the recovery path for a late, early or mistimed tap. It subsumes nudge and
   * backfill without adding a third concept: a correction is just a mark carrying an
   * explicit instant, appended to a log the projection folds over.
   */
  onCorrectStart?: (deltaSeconds: number) => void;
  /**
   * Shorten or lengthen this element by N seconds, mid-run. Owner only, and only on an
   * element the run has not reached yet.
   *
   * This is how time gets made up. The operator is 3 minutes behind, so they take 3
   * minutes out of something downstream — which is an EDIT, not a property of the
   * element. Marking durations "fixed" or "flexible" in the schema would change no
   * arithmetic anywhere; it would only annotate what the operator already knows, at the
   * cost of classifying every element of every timeline across three clients.
   *
   * Editing is otherwise locked during a run to avoid "elements changed under a live
   * occurrence" bugs — but that class is about element IDENTITY (delete, reorder,
   * re-parent) breaking the observation-to-element mapping. Changing a duration touches
   * no identity, and the projection is a pure function of the current elements, so the
   * whole chain simply recomputes.
   */
  onAdjustDuration?: (deltaSeconds: number) => void;
  /**
   * Retract this element's recorded start, which is what unlocks it for editing.
   *
   * Present only on a row the run has reached. Requiring an explicit clear rather than
   * confirming a destructive edit is deliberate: it names what is being undone instead
   * of asking about a side effect, and it cannot be dismissed by reflex.
   */
  onClear?: () => void;
  /**
   * True when the run has recorded a start for this element, so moving or retiming it
   * would leave that observation pointing at something that changed underneath it.
   *
   * The edit affordances are withheld and `onClear` offered instead. Note this is the
   * SAME set as "the run has reached it" — the permission rule and the integrity
   * constraint coincide, which is why this is the right seam rather than a cautious one.
   */
  locked?: boolean;
  /**
   * State that this element is still running, shown on the element immediately BEFORE
   * the playhead.
   *
   * Deliberately an explicit control rather than a press. Pressing a past row could
   * plausibly mean "we're still on that one" or "that started now", and the same
   * gesture meaning two things by position is the sort of implicitness that bites under
   * time pressure.
   */
  onHold?: () => void;
  /** True while this element is the one declared still running. */
  isHeld?: boolean;
  /** How far past its planned hand-off a held element has run, formatted. */
  heldOverBy?: string | null;
}

interface BorderStyle {
  borderColor: string;
  borderWidth: string;
  shadow: string;
}

interface StatusDisplay {
  text: string;
  color: string;
}

/**
 * TimeElementCard - A card component for displaying timeline elements
 *
 * Features:
 * - Left border colored by state (active=blue, expired=gray) or urgency (imminent/soon/moderate)
 * - NextUp elements get prominent styling (thicker border, shadow)
 * - Countdown display for upcoming elements
 * - Optional drag handle and edit/delete buttons
 */
const TimeElementCard: React.FC<TimeElementCardProps> = ({
  element,
  state,
  isNextUp = false,
  urgency,
  showCountdown = false,
  showActualTimes = false,
  formattedStartTime,
  currentTime,
  onEdit,
  onDelete,
  draggable = false,
  onDragStart,
  onDragEnd,
  onDragOver,
  isDragging = false,
  durationNote,
  offsetInfo,
  isGroup = false,
  expanded = false,
  onToggleExpand,
  onUngroup,
  itemCount,
  editMode = false,
  variance,
  runPhase,
  runStatus,
  onMark,
  rowIsMarkTarget = false,
  runRow,
  onCorrectStart,
  onAdjustDuration,
  onClear,
  locked = false,
  onHold,
  isHeld = false,
  heldOverBy = null,
}) => {
  const [correcting, setCorrecting] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  const inRun = runPhase !== undefined;
  const runEnded = runPhase === 'completed';
  // Variance is meaningful for the whole life of a run, INCLUDING after it ends —
  // how it finished is the last thing a viewer sees. Outside a run this is
  // undefined and every branch below is skipped.
  //
  // The one exception is an element the run ended before reaching: its variance is
  // where the forward chain WOULD have put it, which is a projection for something
  // that never happened. "Did not run +0:47 behind" is not a fact about anything.
  const showVariance = inRun
    && typeof variance === 'number'
    && !(runEnded && runStatus === 'upcoming');
  const tone = varianceTone(variance ?? 0);
  // Determine border styling based on state and urgency
  // Uses URGENCY_COLORS from shared package for inline styles (more reliable than Tailwind classes)
  const getBorderStyle = (): BorderStyle => {
    if (state === 'expired') {
      return {
        borderColor: URGENCY_COLORS.EXPIRED,
        borderWidth: 'border-l-2',
        shadow: '',
      };
    }

    if (state === 'active') {
      return {
        borderColor: URGENCY_COLORS.ACTIVE,
        borderWidth: 'border-l-4',
        shadow: 'shadow-lg shadow-blue-500/20',
      };
    }

    // Upcoming elements - style based on urgency and nextUp status
    const urgencyColorMap: Record<UrgencyLevel, string> = {
      imminent: URGENCY_COLORS.IMMINENT,
      soon: URGENCY_COLORS.SOON,
      moderate: URGENCY_COLORS.MODERATE,
      distant: URGENCY_COLORS.DISTANT,
    };

    return {
      borderColor: urgency ? urgencyColorMap[urgency] : URGENCY_COLORS.MODERATE,
      borderWidth: isNextUp ? 'border-l-4' : 'border-l-2',
      shadow: isNextUp ? 'shadow-lg' : '',
    };
  };

  const borderStyle = getBorderStyle();

  // Get status text and color
  //
  // While running, `runStatus` is the source of truth and the legacy state has
  // ALREADY been derived from it by TimeElementList — so these branches agree by
  // construction rather than racing each other. The run wording is used in place
  // of the planned wording (not alongside it): rendering both produced a row
  // labelled "Happening Now" AND "In progress", and a second, unmarked row also
  // claiming "Happening Now".
  const getStatusDisplay = (): StatusDisplay | null => {
    // "Up Next" badge uses urgency color to match iOS
    const urgencyColorMap: Record<UrgencyLevel, string> = {
      imminent: 'text-urgency-imminent',
      soon: 'text-urgency-soon',
      moderate: 'text-urgency-moderate',
      distant: 'text-gray-500 dark:text-gray-400',
    };
    const upNext: StatusDisplay = {
      text: 'Up Next',
      color: urgency ? urgencyColorMap[urgency] : 'text-gray-500 dark:text-gray-400',
    };

    if (inRun && runStatus) {
      if (runStatus === 'current') {
        return { text: 'In progress', color: 'text-state-active' };
      }
      if (runStatus === 'upcoming') {
        // The run ended before reaching this one. It is not "up next" — nothing is.
        if (runEnded) return { text: 'Did not run', color: 'text-gray-500 dark:text-gray-400' };
        return isNextUp ? upNext : null;
      }
      return null;
    }
    if (state === 'active') {
      return { text: 'Happening Now', color: 'text-state-active' };
    }
    if (state === 'upcoming' && isNextUp) {
      return upNext;
    }
    return null;
  };

  const statusDisplay = getStatusDisplay();

  // Calculate progress for active elements
  const getActiveProgress = (): { progress: number; remaining: number } => {
    if (state !== 'active' || !currentTime) return { progress: 0, remaining: 0 };
    return {
      progress: calculateProgress(currentTime, element.startTime, element.duration),
      remaining: calculateTimeRemaining(currentTime, element.endTime)
    };
  };

  const activeProgress = getActiveProgress();

  // Countdown color based on state
  // NextUp: primary text color (prominent)
  // Others: urgency color matching left border
  const getCountdownColor = (): string => {
    if (isNextUp) return 'text-gray-900 dark:text-white';
    switch (urgency) {
      case 'imminent': return 'text-urgency-imminent';
      case 'soon': return 'text-urgency-soon';
      case 'moderate': return 'text-urgency-moderate';
      default: return 'text-gray-500 dark:text-gray-400';
    }
  };

  // The row is a mark target only for an owner during a live run. Clicks that started
  // on a real control inside the row (the flag, the overflow menu, the group chevron)
  // belong to that control and must not also mark.
  const markable = rowIsMarkTarget && !!onMark;
  const handleRowActivate = (e: React.MouseEvent | React.KeyboardEvent) => {
    if (!markable) return;
    if ((e.target as HTMLElement)?.closest('button, a, input, select, textarea')) return;
    onMark!();
  };

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      role={markable ? 'button' : undefined}
      tabIndex={markable ? 0 : undefined}
      aria-label={markable ? `Mark ${element.description} as started now` : undefined}
      onClick={markable ? handleRowActivate : undefined}
      onKeyDown={markable ? (e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleRowActivate(e); }
      } : undefined}
      className={`
        relative rounded-lg bg-white dark:bg-gray-800
        border border-gray-200 dark:border-gray-700
        ${borderStyle.borderWidth}
        ${borderStyle.shadow}
        ${draggable && !isDragging ? 'cursor-move' : ''}
        ${markable ? 'cursor-pointer hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-1' : ''}
        ${isDragging ? 'opacity-40' : ''}
        transition-all duration-200
        p-3 sm:p-4
      `}
      style={{ borderLeftColor: borderStyle.borderColor }}
    >
      <div className="flex items-center justify-between gap-3">
        {/* Left side: drag handle + content */}
        <div className="flex items-center gap-3 flex-1 min-w-0">
          {/* Drag handle */}
          {draggable && (
            <GripVertical className="w-5 h-5 text-gray-500 flex-shrink-0" />
          )}

          {/* Main content */}
          <div className="flex-1 min-w-0">
            {/* Element name and status — for a group, the disclosure chevron sits inline, just
                before the title (as if part of the name), so cards line up down the left edge. */}
            <div className="flex items-center gap-1.5 flex-wrap">
              {isGroup && onToggleExpand && (
                <button
                  onClick={(e) => { e.stopPropagation(); onToggleExpand(); }}
                  aria-label={expanded ? 'Collapse group' : 'Expand group'}
                  aria-expanded={expanded}
                  className="-ml-0.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 flex-shrink-0"
                >
                  <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </button>
              )}
              <span className="text-gray-800 dark:text-gray-100 font-medium text-sm sm:text-base break-words">
                {element.description}
              </span>
              {/* A run always labels its rows, even where the planned view would
                  suppress the badge for want of a countdown. */}
              {(showCountdown || inRun) && statusDisplay && (
                <span className={`text-xs sm:text-sm ${statusDisplay.color}`}>
                  {statusDisplay.text}
                </span>
              )}
              {/* Three independent channels — glyph, words, colour — so the chip reads
                  in greyscale, on a washed-out projector, and to a colour-blind operator.
                  Unsigned: the word carries direction, and "−16:50 ahead" carried it
                  twice, contradicting itself for anyone who resolved the sign first. */}
              {showVariance && tone !== 'on-time' && (
                <span className={`text-xs sm:text-sm font-mono font-semibold ${varianceColorClass(tone)}`}>
                  <span aria-hidden="true">{varianceGlyph(tone)} </span>
                  {formatVarianceWords(variance as number)}
                </span>
              )}
            </div>

            {/* Duration — or, for the element actually in progress, elapsed against
                budget, which is the live fact. "Duration: 5:00" is authoring
                information and says nothing about where the run has got to. */}
            {runRow?.ranFor ? null : runRow?.elapsed && runRow?.budget ? (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
                <span className="font-mono font-medium">{runRow.elapsed}</span>
                <span className="mx-1 text-gray-400 dark:text-gray-500">of</span>
                <span className="font-mono font-medium">{runRow.budget}</span>
                {runRow.dueToEndClock && (
                  <span className="ml-2 text-gray-400 dark:text-gray-500">
                    · due to end ~<span className="font-medium">{runRow.dueToEndClock}</span>
                  </span>
                )}
              </div>
            ) : (
            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">
              Duration:{' '}
              {onAdjustDuration ? (
                <button
                  onClick={(e) => { e.stopPropagation(); setAdjusting((a) => !a); }}
                  aria-expanded={adjusting}
                  title="Adjust this element's length to make up time"
                  className="font-mono font-medium underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                >
                  {formatDuration(element.duration)}
                </button>
              ) : (
                <span className="font-mono font-medium">{formatDuration(element.duration)}</span>
              )}
              {durationNote && (
                <span className="ml-2 text-purple-500 dark:text-purple-400 text-xs">{durationNote}</span>
              )}
              {isGroup && typeof itemCount === 'number' && (
                <span className="ml-2 text-xs text-gray-400 dark:text-gray-500">· {itemCount} item{itemCount !== 1 ? 's' : ''}</span>
              )}
            </div>
            )}

            {/* "Still running" — the statement a mark cannot make. A mark says when
                something BEGAN and nothing about whether it has finished, which is why
                without this the app can only learn of an over-run after the fact. */}
            {onHold && !isHeld && (
              <button
                onClick={(e) => { e.stopPropagation(); onHold(); }}
                className="mt-1.5 inline-flex items-center gap-1.5 rounded-md border border-amber-300 dark:border-amber-700 px-2 py-1 text-xs font-medium text-amber-700 dark:text-amber-400 transition-colors hover:bg-amber-50 dark:hover:bg-amber-900/30"
              >
                <AlertTriangle className="h-3.5 w-3.5" aria-hidden="true" />
                Still running
              </button>
            )}

            {isHeld && (
              <div className="mt-1.5 flex items-center gap-2 text-xs sm:text-sm text-amber-700 dark:text-amber-400">
                <span className="relative flex h-2 w-2" aria-hidden="true">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-75" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-amber-500" />
                </span>
                <span className="font-medium">Still running</span>
                {heldOverBy && (
                  <span className="font-mono">over by {heldOverBy}</span>
                )}
                <span className="text-gray-400 dark:text-gray-500">
                  · mark the next element to release
                </span>
              </div>
            )}

            {/* Make up time by trimming something the run has not reached yet. Same
                control as the start correction above — one idiom for "adjust a time",
                pointed forwards instead of backwards. */}
            {adjusting && onAdjustDuration && (
              <div
                className="mt-1.5 flex flex-wrap items-center gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {[-60, -30, 30, 60].map((delta) => (
                  <button
                    key={delta}
                    onClick={(e) => { e.stopPropagation(); onAdjustDuration(delta); }}
                    aria-label={`${delta > 0 ? 'Lengthen' : 'Shorten'} ${element.description} by ${Math.abs(delta)} seconds`}
                    className="px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-mono text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {delta > 0 ? '+' : '−'}{Math.abs(delta) >= 60 ? `${Math.abs(delta) / 60}m` : `${Math.abs(delta)}s`}
                  </button>
                ))}
                <button
                  onClick={(e) => { e.stopPropagation(); setAdjusting(false); }}
                  className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Done
                </button>
              </div>
            )}

            {/* Completed: what actually happened, measured — not projected. */}
            {runRow?.ranFor && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                ran <span className="font-mono font-medium">{runRow.ranFor}</span>
                {runRow.spanFrom && runRow.spanTo && (
                  <span className="ml-2 text-gray-400 dark:text-gray-500 font-mono">
                    {onCorrectStart ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); setCorrecting((c) => !c); }}
                        aria-expanded={correcting}
                        title="Correct the recorded start time"
                        className="underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                      >
                        {runRow.spanFrom}
                      </button>
                    ) : runRow.spanFrom}
                    {' → '}{runRow.spanTo}
                  </span>
                )}
              </div>
            )}

            {/* Start time — suppressed when a run template already states it. A
                completed row carries its measured span and an anchor carries its fixed
                time; printing "Started at" underneath either says the same thing twice
                and pushes the live facts down the row. */}
            {showActualTimes && formattedStartTime && !runRow?.ranFor && !runRow?.anchorClock && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {/* A row that was never marked, or that the run ended before
                    reaching, has no actual start — the time shown is the PLANNED
                    one, so claiming it "Started at" contradicts its own badge. */}
                {inRun && (runStatus === 'skipped' || (runStatus === 'upcoming' && runEnded))
                  ? 'Was due at: '
                  : state === 'upcoming' ? 'Starts at: ' : 'Started at: '}
                {onCorrectStart ? (
                  <button
                    onClick={(e) => { e.stopPropagation(); setCorrecting((c) => !c); }}
                    aria-expanded={correcting}
                    title="Correct the recorded start time"
                    className="font-medium underline decoration-dotted underline-offset-2 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                  >
                    {formattedStartTime}
                  </button>
                ) : (
                  <span className="font-medium">
                    {/* A tilde marks a projection that will move as the run drifts. An
                        anchor never gets one: its time is the one thing that holds. */}
                    {runRow?.projected ? '~' : ''}{formattedStartTime}
                  </span>
                )}
              </div>
            )}

            {/* Correct in place. Pre-filled with what was recorded, so the operator
                adjusts rather than composes — there is no time to type a timestamp
                while a service is running. */}
            {correcting && onCorrectStart && (
              <div
                className="mt-1.5 flex flex-wrap items-center gap-1.5"
                onClick={(e) => e.stopPropagation()}
              >
                {[-60, -10, 10, 60].map((delta) => (
                  <button
                    key={delta}
                    onClick={(e) => { e.stopPropagation(); onCorrectStart(delta); }}
                    aria-label={`Shift start ${delta > 0 ? 'later' : 'earlier'} by ${Math.abs(delta)} seconds`}
                    className="px-2 py-1 rounded-md border border-gray-300 dark:border-gray-600 text-xs font-mono text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  >
                    {delta > 0 ? '+' : '−'}{Math.abs(delta) >= 60 ? `${Math.abs(delta) / 60}m` : `${Math.abs(delta)}s`}
                  </button>
                ))}
                <button
                  onClick={(e) => { e.stopPropagation(); setCorrecting(false); }}
                  className="px-2 py-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
                >
                  Done
                </button>
              </div>
            )}
            {!showActualTimes && formattedStartTime && (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 font-mono font-semibold">
                {formattedStartTime}
              </div>
            )}

            {/* Offset info (for fixed elements). During a run this becomes the
                wall-clock time the anchor holds — "+35m from occurrence start" is how
                the element was AUTHORED, not what the operator needs at 10:07. */}
            {runRow?.anchorClock ? (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                <span className="uppercase tracking-[0.12em] text-[0.65rem] text-gray-400 dark:text-gray-500 mr-1.5">
                  Fixed start
                </span>
                <span className="font-medium">{runRow.anchorClock}</span>
              </div>
            ) : offsetInfo ? (
              <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                {offsetInfo}
              </div>
            ) : null}

            {/* Anchors are warned about, never enforced (plan §8 Q2). */}
            {runRow?.anchorRisk && (
              <div className="flex items-start gap-1.5 text-xs sm:text-sm text-amber-700 dark:text-amber-400 mt-0.5">
                <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" aria-hidden="true" />
                <span>{runRow.anchorRisk}</span>
              </div>
            )}

            {/* Countdown — on mobile it lives here (full width) instead of the cramped right gutter.
                `countdown === undefined` means no projection: show nothing rather than
                "0:00", which reads as "imminent" when the truth is "unknown". */}
            {showCountdown && element.countdown !== undefined && state !== 'expired' && state !== 'active' && (
              <div className={`sm:hidden mt-1 text-base font-mono font-bold ${getCountdownColor()}`}>
                {utilityService.formatCountdown(element.countdown)}
              </div>
            )}

            {/* Progress through the element in progress.
                During a run this is driven by the ACTUAL start and knows about
                over-run, so it agrees with the display panel's NOW strip. The legacy
                clock-derived version has no concept of running over: it pinned to 100%
                and reported "0:00 remaining" on a row that had been going a minute
                longer than its budget. */}
            {runRow?.progress !== undefined ? (
              <div className="mt-2">
                <div className="h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ease-linear ${
                      runRow.overBy ? 'bg-amber-500' : 'bg-state-active'
                    }`}
                    style={{ width: `${Math.min(1, Math.max(0, runRow.progress)) * 100}%` }}
                  />
                </div>
                <div className={`text-xs mt-1 ${runRow.overBy ? 'text-amber-600 dark:text-amber-400' : 'text-state-active'}`}>
                  {runRow.overBy ? `over by ${runRow.overBy}` : `${runRow.remaining ?? ''} remaining`}
                </div>
              </div>
            ) : state === 'active' && currentTime ? (
              <div className="mt-2">
                {/* Progress bar track */}
                <div className="h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full overflow-hidden">
                  {/* Progress fill */}
                  <div
                    className="h-full bg-state-active rounded-full transition-all duration-500 ease-linear"
                    style={{ width: `${activeProgress.progress * 100}%` }}
                  />
                </div>
                {/* Remaining time */}
                <div className="text-xs text-state-active mt-1">
                  {utilityService.formatCountdown(activeProgress.remaining)} remaining
                </div>
              </div>
            ) : null}
          </div>
        </div>

        {/* Right side: countdown + actions */}
        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Countdown (desktop/tablet only — mobile renders it inside the content) */}
          {showCountdown && element.countdown !== undefined && state !== 'expired' && state !== 'active' && (
            <span className={`hidden sm:inline text-xl md:text-2xl font-mono font-bold ${getCountdownColor()}`}>
              {utilityService.formatCountdown(element.countdown)}
            </span>
          )}

          {/* Mark this element as started. Operate mode only — edit mode owns the
              row overflow menu, and the two must not compete for the gutter. */}
          {onMark && !editMode && (
            <button
              onClick={(e) => { e.stopPropagation(); onMark(); }}
              title={`Mark ${element.description} as started`}
              aria-label={`Mark ${element.description} as started`}
              className="p-2 rounded-lg text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-all"
            >
              <Flag className="w-4 h-4" />
            </button>
          )}

          {/* Per-row overflow (⋯) menu.
              In edit mode it carries the usual actions — except on a LOCKED row, where
              the run has recorded a start and editing would invalidate it. Such a row
              offers Clear instead: retract the observation and it becomes editable like
              anything the run has not reached.
              Clear is also offered outside edit mode, because un-recording a mis-mark is
              an operator action, not an authoring one. */}
          {(editMode || (locked && onClear)) && (() => {
            const editActions: RowAction[] = isGroup
              ? [
                  onEdit && { label: 'Rename', onClick: onEdit, icon: <Edit3 className="w-4 h-4" /> },
                  onUngroup && { label: 'Ungroup', onClick: onUngroup, icon: <Ungroup className="w-4 h-4" /> },
                ].filter(Boolean) as RowAction[]
              : [
                  onEdit && { label: 'Edit', onClick: onEdit, icon: <Edit3 className="w-4 h-4" /> },
                  onDelete && { label: 'Delete', onClick: onDelete, icon: <Trash2 className="w-4 h-4" />, danger: true },
                ].filter(Boolean) as RowAction[];
            const clearAction: RowAction[] = onClear
              ? [{ label: 'Clear', onClick: onClear, icon: <Ungroup className="w-4 h-4" />, danger: true }]
              : [];
            const actions = locked ? clearAction : [...(editMode ? editActions : []), ...clearAction];
            return <RowActionsMenu actions={actions} label={isGroup ? 'Group actions' : 'Element actions'} />;
          })()}
        </div>
      </div>
    </div>
  );
};

export default TimeElementCard;
